// src/pages/appointments/success/AppointmentSuccess.jsx //
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { BasicDialogFrame } from "@/components/ui/BasicDialog.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import { patchOwnNotificationPreferences } from "@/services/notificationPreferencesService.js";

// If this import path differs in your project, adjust it to whatever file
// you use to render the booked doctor card behind the overlay.
import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";

function formatDisplayTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/[ap]m/i.test(raw)) return raw.replace(/\s+/g, " ").trim();

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  const hours24 = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours24)) return raw;

  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes} ${suffix}`;
}

function formatVisitTypeLabel(mode, consultationType, t = (_, fallback) => fallback) {
  const normalizedMode = String(mode || "").trim().toLowerCase();
  if (normalizedMode === "evisit") {
    return t("appointments.success.visitType.virtual", "Virtual appointment");
  }
  if (normalizedMode === "inperson") {
    return t("appointments.success.visitType.inperson", "In-person appointment");
  }
  return String(consultationType || "").trim().toLowerCase() === "private"
    ? t("appointments.success.visitType.virtual", "Virtual appointment")
    : t("appointments.success.visitType.inperson", "In-person appointment");
}

export default function AppointmentSuccess() {
  const nav = useNavigate();
  const location = useLocation();
  const { t, formatDate } = useLanguage();
  const { clearBooking } = useAppointmentsFilters();

  const doctor = React.useMemo(() => {
    const base = location.state?.doctor ?? null;
    if (!base) return null;
    const doctorId = String(base?.id || location.state?.doctorId || "").trim();
    return doctorId ? { ...base, id: doctorId } : base;
  }, [location.state]);
  const appointmentId = location.state?.appointmentId ?? null;
  const amountPaid = location.state?.amountPaid ?? null;
  const currency = location.state?.currency ?? doctor?.currency ?? "ZAR";
  const paymentLabel = location.state?.paymentLabel ?? null;
  const insurancePaymentMode = location.state?.insurancePaymentMode ?? "self_pay";
  const insuranceProvider = location.state?.insuranceProvider ?? "";
  const insuranceContextLabel = location.state?.insuranceContextLabel ?? (insurancePaymentMode === "insurance"
    ? t("appointments.checkout.insurance.generic", "Insurance selected")
    : t("appointments.checkout.insurance.selfPay", "Self-pay"));
  const insuranceProviderOptions = location.state?.insuranceProviderOptions ?? [];
  const insuranceAcceptedLabel = location.state?.insuranceAcceptedLabel ?? "";
  const serviceId = location.state?.serviceId ?? null;
  const serviceName = location.state?.serviceName ?? "";
  const serviceNamePrice = location.state?.serviceNamePrice ?? "";
  const when = location.state?.when ?? null; // { dateISO, time }
  const durationMinutes =
    location.state?.durationMinutes ??
    location.state?.duration_minutes ??
    location.state?.duration ??
    when?.duration_mins ??
    null;
  const mode = location.state?.mode ?? null;
  const patient = location.state?.patient ?? null;
  const facilityId = location.state?.facilityId ?? null;
  const facilityName = location.state?.facilityName ?? "";
  const facility = location.state?.facility ?? null;
  const consultationType = location.state?.consultation_type ?? null;
  const practiceId = location.state?.practice_id ?? null;
  const [showReminderNotice, setShowReminderNotice] = React.useState(
    Boolean(location.state?.appointmentRemindersDefaulted),
  );
  const [confirmReminderDeactivation, setConfirmReminderDeactivation] = React.useState(false);
  const [savingReminderPreference, setSavingReminderPreference] = React.useState(false);
  const [appointmentReminderRevision, setAppointmentReminderRevision] = React.useState(
    location.state?.appointmentReminderRevision || null,
  );
  const visitTypeLabel = formatVisitTypeLabel(mode, consultationType, t);
  const appointmentDateLabel = when?.dateISO
    ? formatDate(when.dateISO, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const appointmentMetaLabel = [appointmentDateLabel, formatDisplayTime(when?.time), visitTypeLabel]
    .filter(Boolean)
    .join(" • ");
  const successDoctorCard = React.useMemo(
    () =>
      doctor
        ? {
            ...doctor,
            appointmentLabel: appointmentMetaLabel || doctor?.appointmentLabel || "",
          }
        : null,
    [appointmentMetaLabel, doctor],
  );

  React.useEffect(() => {
    clearBooking?.();
  }, [clearBooking]);

  React.useEffect(() => {
    window.dispatchEvent(new Event("afrimed:appointments:changed"));
  }, []);

  const deactivateAppointmentReminders = React.useCallback(async () => {
    setSavingReminderPreference(true);
    try {
      const { data, error } = await patchOwnNotificationPreferences({
        role: "patient",
        expectedUpdatedAt: appointmentReminderRevision,
        patch: {
          appointment_reminders: {
            enabled: false,
            channels: { in_app: false, email: false, sms: false, push: false },
          },
        },
        source: "appointment_booking_opt_out",
      });
      if (error) {
        console.error("Appointment reminder opt-out failed", error);
        return;
      }
      setAppointmentReminderRevision(data?.updated_at || null);
      setConfirmReminderDeactivation(false);
      setShowReminderNotice(false);
    } finally {
      setSavingReminderPreference(false);
    }
  }, [appointmentReminderRevision]);

  const goDetails = React.useCallback(() => {
  if (!appointmentId) return nav("/appointments", { replace: true });

  try {
    // ✅ toast key matches the Details route param (appointmentId)
    sessionStorage.setItem(
      `afrimed:toast:appointmentConfirmed:${String(appointmentId)}`,
      "1"
    );

    // ✅ persist snapshot for Details restore
      const payload = {
        doctor,
        patient,
        appointmentId,
        amountPaid,
        currency,
        paymentLabel,
        insurancePaymentMode,
        insuranceProvider,
        insuranceContextLabel,
        insuranceProviderOptions,
        insuranceAcceptedLabel,
        serviceId,
        serviceName,
        serviceNamePrice,
        paymentMethodLabel: paymentLabel,
        selectedDate: when?.dateISO ?? null,
        selectedTime: when?.time ?? null,
        when,
        mode,
        facilityId,
        facilityName,
        facility,
        consultation_type: consultationType,
        practice_id: practiceId,
        duration: durationMinutes,
        durationMinutes,
        duration_minutes: durationMinutes,
        justConfirmed: true,
      };

    sessionStorage.setItem(
      `afrimed:appointment:${String(appointmentId)}`,
      JSON.stringify(payload)
    );
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    sessionStorage.setItem(
      `afrimed:doctor_appt:${String(appointmentId)}`,
      JSON.stringify(payload)
    );
    localStorage.setItem(
      `afrimed:appointment:${String(appointmentId)}`,
      JSON.stringify(payload)
    );
    localStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    localStorage.setItem(
      `afrimed:doctor_appt:${String(appointmentId)}`,
      JSON.stringify(payload)
    );
    if (doctor?.id) {
      sessionStorage.setItem(`afrimed:doctor:${String(doctor.id)}`, JSON.stringify(doctor));
      sessionStorage.setItem("afrimed:lastDoctor", JSON.stringify(doctor));
      localStorage.setItem(`afrimed:doctor:${String(doctor.id)}`, JSON.stringify(doctor));
      localStorage.setItem("afrimed:lastDoctor", JSON.stringify(doctor));
    }
  } catch {}

  nav(`/appointments/details/${appointmentId}`, {
    // replace: true,
    state: {
      doctor,
      patient,
      appointmentId,
      amountPaid,
      currency,
      paymentLabel,
      insurancePaymentMode,
      insuranceProvider,
      insuranceContextLabel,
      insuranceProviderOptions,
      insuranceAcceptedLabel,
      serviceId,
      serviceName,
      serviceNamePrice,
      paymentMethodLabel: paymentLabel,
      selectedDate: when?.dateISO ?? null,
      selectedTime: when?.time ?? null,
      when,
      mode,
      facilityId,
      facilityName,
      facility,
      consultation_type: consultationType,
      practice_id: practiceId,
      duration: durationMinutes,
      durationMinutes,
      duration_minutes: durationMinutes,
      justConfirmed: true,
    },
  });
}, [nav, appointmentId, doctor, patient, amountPaid, currency, paymentLabel, insurancePaymentMode, insuranceProvider, insuranceContextLabel, insuranceProviderOptions, insuranceAcceptedLabel, serviceId, serviceName, serviceNamePrice, when, mode, facilityId, facilityName, facility, consultationType, practiceId, durationMinutes]);

  return (
    <>
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      {/* Background content (doctor card) */}
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.success.confirmationTitle", "Appointment confirmation")}
          onBack={() => nav(-1)}
        />

        {successDoctorCard ? (
          <div className="mt-4">
            <DoctorResultCard doctor={successDoctorCard} />
          </div>
        ) : (
          <div className="mt-4 opacity-60 text-body-medium text-on-surface-var" />
        )}
      </div>

      {/* Overlay */}
      <div className="fixed inset-0 z-50">
        <div
          className="absolute inset-0 bg-[rgba(2,6,23,0.32)]"
          aria-hidden="true"
          onClick={goDetails}
        />

        <div className="absolute inset-0 flex items-start md:items-center justify-center px-4">
          <div className="w-full flex justify-center pt-24 md:pt-0 pb-8 md:pb-0">
            <div
              className="
                relative
                w-full max-w-[380px]
                rounded-[16px]
                bg-[rgb(var(--clr-surface-cont-highest))]
                shadow-[0_12px_40px_rgba(2,6,23,0.18)]
                px-6 pt-7 pb-6
                text-center
                translate-y-[20vh] md:translate-y-0
              "
              style={{ maxHeight: "calc(100dvh - 48px)", overflow: "auto" }}
              role="dialog"
              aria-modal="true"
            >
              {/* X close (blueprint: go to details, never “close and stay here”) */}
              <button
                type="button"
                onClick={goDetails}
                aria-label={t("common.close", "Close")}
                className="
                  absolute top-4 right-4 z-50
                  h-10 w-10
                  rounded-[12px]
                  bg-[rgba(2,6,23,0.06)]
                  hover:bg-[rgba(2,6,23,0.10)]
                  active:bg-[rgba(2,6,23,0.10)]
                  flex items-center justify-center
                "
              >
                <img
                  src="/icons/doctor/close.svg"
                  alt=""
                  className="
                    h-6 w-6 opacity-80 block
                    translate-x-[2.5px] translate-y-[2.5px]
                  "
                  draggable="false"
                />
              </button>

              <div className="relative flex justify-center">
                <img
                  src="/icons/filter-doctors/appointments_completed_shield.svg"
                  alt=""
                  className="w-[203px] h-[203px]"
                  draggable="false"
                />

                {/* Animated check mark (stroke-draw, centered, no clipping) */}
                <svg
                  viewBox="0 0 25 25"
                  className="absolute w-[60px] h-[60px] md:w-[65px] md:h-[65px] overflow-visible"
                  style={{
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                  aria-hidden="true"
                >
                  <path
                    d="M3.44 13.527 L9.078 19.165 L21.182 7.061"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    pathLength="100"
                    className="animate-afrimed-check"
                  />
                </svg>
              </div>

              <div className="mt-4 text-headline-medium text-on-surface">
                {t("appointments.success.title", "Appointment completed")}
              </div>

              <div className="mt-2 text-body-medium text-on-surface-var">
                {t(
                  "appointments.success.body",
                  "Your booking has been created. You can open it from your appointments now."
                )}
              </div>
              {/* No CTA — blueprint uses X/back */}
            </div>
          </div>
        </div>
      </div>
      </div>
      {showReminderNotice ? (
      <div className="fixed inset-0 z-[260] flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/35" />
        <BasicDialogFrame
          title={t("appointments.reminders.active.title", "Appointment reminders are on")}
          body={t(
            "appointments.reminders.active.copy",
            "AfriMed™ will send reminders before your appointment. You can change this at any time in Notifications.",
          )}
        >
          <button
            type="button"
            className="auth-social-button min-h-12 flex-1 px-4 text-label-large"
            onClick={() => {
              setShowReminderNotice(false);
              setConfirmReminderDeactivation(true);
            }}
          >
            {t("appointments.reminders.active.turnOff", "Turn reminders off")}
          </button>
          <button
            type="button"
            className="min-h-12 flex-1 rounded-[12px] bg-[rgb(var(--afrimed-primary))] px-4 text-label-large text-[rgb(var(--afrimed-on-primary))]"
            onClick={() => setShowReminderNotice(false)}
          >
            {t("appointments.reminders.active.action", "Got it")}
          </button>
        </BasicDialogFrame>
      </div>
      ) : null}
      {confirmReminderDeactivation ? (
        <BasicDialogCritical
          open
          title={t(
            "appointments.reminders.deactivate.title",
            "Are you sure you want to switch off reminders for this appointment?",
          )}
          body={t(
            "appointments.reminders.deactivate.copy",
            "If you turn off reminders for this appointment, you may miss the appointment.",
          )}
          iconSrc="/icons/clinicians/report.svg"
          cancelText={t("appointments.reminders.deactivate.keep", "Keep reminders on")}
          okText={t("appointments.reminders.deactivate.confirm", "Switch off reminders")}
          disableBackdropClose={savingReminderPreference}
          onCancel={() => !savingReminderPreference && setConfirmReminderDeactivation(false)}
          onOk={() => !savingReminderPreference && deactivateAppointmentReminders()}
        />
      ) : null}
    </>
  );
}
